import type { GatewayClient } from '../gateway';
import type { ServerToClientMessage } from '../types';
import { useSymbolStore, normalizeServerSymbol } from '../../stores/symbolStore';
import type {
  ActiveSymbol,
  AvailableSymbol,
  ServiceCapacity,
} from '../../stores/symbolStore';
import { useTickerStore } from '../../stores/tickerStore';
import type { TickerEntry } from '../../stores/tickerStore';
import { useTradeStore } from '../../stores/tradeStore';
import { useBookStore } from '../../stores/bookStore';

function toCode(symbol: string): string {
  return normalizeServerSymbol(symbol, useSymbolStore.getState().wsToCode);
}

export function dispatchMarketMessage(
  msg: ServerToClientMessage,
  _gateway?: GatewayClient,
): boolean {
  switch (msg.type) {
    case 'snapshot': {
      if (msg.kind === 'symbols' && Array.isArray(msg.data)) {
        useSymbolStore.getState().setAvailable(msg.data as AvailableSymbol[]);
      } else if (msg.kind === 'active') {
        if (Array.isArray(msg.data)) {
          useSymbolStore.getState().setActive(msg.data as ActiveSymbol[]);
        } else if (
          msg.data &&
          typeof msg.data === 'object' &&
          Array.isArray((msg.data as Record<string, unknown>).active)
        ) {
          const d = msg.data as {
            active: ActiveSymbol[];
            capacity?: ServiceCapacity;
          };
          useSymbolStore.getState().setActive(d.active, d.capacity);
        }
      } else if (msg.kind === 'ticker' && Array.isArray(msg.data)) {
        useTickerStore.getState().setTickers(msg.data as TickerEntry[]);
      } else if (msg.kind === 'full' && msg.data && typeof msg.data === 'object') {
        const d = msg.data as Record<string, unknown>;
        if (Array.isArray(d.available)) {
          useSymbolStore.getState().setAvailable(d.available as AvailableSymbol[]);
        }
        if (Array.isArray(d.active)) {
          useSymbolStore.getState().setActive(
            d.active as ActiveSymbol[],
            d.capacity as ServiceCapacity | undefined,
          );
        }
        if (Array.isArray(d.tickers)) {
          useTickerStore.getState().setTickers(d.tickers as TickerEntry[]);
        }
      }
      return true;
    }
    case 'ticker-update':
      useTickerStore.getState().setTickers(msg.tickers as TickerEntry[]);
      return true;
    case 'trade': {
      const tradeEntry = {
        price: msg.data.price,
        qty: msg.data.qty,
        side: msg.data.side,
        ts: msg.data.ts,
        ...(msg.data.tradeId != null ? { tradeId: msg.data.tradeId } : {}),
      };
      useTradeStore.getState().addTrade(toCode(msg.symbol), tradeEntry);
      return true;
    }
    case 'trade-batch': {
      const store = useTradeStore.getState();
      const code = toCode((msg as { symbol: string }).symbol);
      for (const t of (
        msg as {
          symbol: string;
          data: Array<{
            price: string;
            qty: string;
            side: 'buy' | 'sell';
            ts: number;
            tradeId?: string;
          }>;
        }
      ).data) {
        store.addTrade(code, {
          price: t.price,
          qty: t.qty,
          side: t.side,
          ts: t.ts,
          ...(t.tradeId != null ? { tradeId: t.tradeId } : {}),
        });
      }
      return true;
    }
    case 'trade-snapshot':
      useTradeStore.getState().setTrades(toCode(msg.symbol), [...msg.trades].reverse());
      return true;
    case 'book-snapshot':
      // B3 fix: book frames carry `symbol` (wsSymbol). Re-key by raw code so the
      // now per-symbol bookStore (Record<symbol, SymbolBookState>) stays isolated.
      useBookStore.getState().setSnapshot(toCode(msg.symbol), msg.bids, msg.asks);
      return true;
    case 'book-update':
      useBookStore.getState().applyDelta(toCode(msg.symbol), msg.bids, msg.asks);
      return true;
    case 'symbol-progress':
      useSymbolStore
        .getState()
        .updateProgress(msg.jobKey, msg.phase, msg.progress, msg.message);
      return true;
    default:
      return false;
  }
}
